import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, FileText, Shield } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 pb-20">
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8" />
                <h1 className="text-2xl font-bold">Terms of Service</h1>
              </div>
              <p className="text-blue-100 mt-1">Last updated: January 19, 2025</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-8 prose prose-blue dark:prose-invert max-w-none text-slate-900 dark:text-slate-100">
            <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing and using Hines Time ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, you should not use this service.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">2. Description of Service</h2>
            <p className="mb-4">
              Hines Time provides workforce time tracking, job management, location verification, and payroll reporting services ("the Service"). The Service is provided on a subscription basis with a 30-day free trial period.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">3. Account Registration</h2>
            <p className="mb-4">
              To use the Service, you must:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Register for an account by providing accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and accept responsibility for all activities under your account</li>
              <li>Provide a valid payment method for billing after the trial period</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">4. Subscription and Billing</h2>
            <p className="mb-4">
              <strong>Free Trial:</strong> New accounts receive a 30-day free trial. A valid credit card is required during registration. You will not be charged during the trial period.
            </p>
            <p className="mb-4">
              <strong>Pricing:</strong> After the trial period, you will be automatically charged:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>One-time setup fee: $9.99 USD (or equivalent in CAD/EUR)</li>
              <li>Monthly subscription: $5.99 per active user (same numeric amount applies in USD, CAD, and EUR — symbol differs per currency)</li>
            </ul>
            <p className="mb-4">
              <strong>Metered Billing:</strong> Your monthly bill is calculated based on the number of active users in your account. Adding or removing users will automatically adjust your next billing cycle.
            </p>
            <p className="mb-4">
              <strong>Payment:</strong> All payments are processed securely through Stripe. We do not store your credit card information.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">5. Cancellation and Refunds</h2>
            <p className="mb-4">
              You may cancel your subscription at any time. Cancellations take effect at the end of the current billing period. We do not provide refunds for partial months or the setup fee.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">6. User Data and Privacy</h2>
            <p className="mb-4">
              We collect and process data as described in our Privacy Policy. This includes:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>User account information (name, email, phone)</li>
              <li>Time tracking data (clock in/out times, GPS locations)</li>
              <li>Job information and photo documentation</li>
              <li>Team and company information</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">7. Acceptable Use</h2>
            <p className="mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Share your account credentials with others</li>
              <li>Use the Service to transmit viruses or malicious code</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">8. Intellectual Property</h2>
            <p className="mb-4">
              HinesTime is developed by <strong>Scalevisory Inc.</strong> All intellectual property rights in the Service, 
              including but not limited to the software, code, features, functionality, design, graphics, user interface, 
              and documentation, are owned exclusively by <strong>Scalevisory Inc.</strong>
            </p>
            <p className="mb-4">
              The HinesTime name, logo, and all related names, logos, product and service names, designs, and slogans 
              are trademarks of Scalevisory Inc. You may not use such marks without the prior written permission of 
              Scalevisory Inc.
            </p>
            <p className="mb-4">
              All content, features, and functionality of the Service are protected by international copyright, trademark, 
              patent, trade secret, and other intellectual property or proprietary rights laws. No part of the Service may 
              be copied, reproduced, distributed, republished, downloaded, displayed, posted, or transmitted in any form 
              without the express written permission of Scalevisory Inc.
            </p>
            <p className="mb-4">
              <strong>© 2025 Scalevisory Inc. All rights reserved.</strong>
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">9. Limitation of Liability</h2>
            <p className="mb-4">
              Scalevisory Inc. and HinesTime shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">10. Changes to Terms</h2>
            <p className="mb-4">
              We reserve the right to modify these terms at any time. We will notify users of any material changes via email. Your continued use of the Service after changes constitutes acceptance of the modified terms.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">11. Contact Information</h2>
            <p className="mb-4">
              For questions about these Terms of Service, please contact us at:
            </p>
            <p className="mb-4">
              <strong>Scalevisory Inc.</strong><br />
              Email: legal@hinestime.com<br />
              Support: support@hinestime.com<br />
              Address: Calgary, Alberta, Canada
            </p>

            <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 rounded-r-lg">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                <Shield className="w-5 h-5 inline mr-2" />
                <strong>By using HinesTime, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. 
                HinesTime is a product of Scalevisory Inc.</strong>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
